/**
 * ReviewPrompt — appears 24+ hours after event end to request a review.
 * Triggered by a sentinel check on app load; dismissible per booking.
 */
import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';

const DISMISSED_KEY = 'snt_reviews_dismissed_v1';

export default function ReviewPrompt() {
  const [booking, setBooking] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Skip if user not logged in
    if (!localStorage.getItem('snt_token')) return;
    // Find a recent attended booking awaiting review
    api.get('/api/me/bookings/pending-review').then(r => {
      if (!r.bookings?.length) return;
      const dismissed = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
      const next = r.bookings.find(b => !dismissed.includes(b.id));
      if (next) setBooking(next);
    }).catch(() => {});
  }, []);

  function dismiss() {
    const dismissed = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
    dismissed.push(booking.id);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
    setBooking(null);
  }

  async function submit() {
    if (rating === 0) return;
    setSubmitting(true);
    try {
      await api.post(`/api/bookings/${booking.id}/review`, { rating, comment });
      dismiss();
    } catch (e) { alert(e.message); }
    finally { setSubmitting(false); }
  }

  if (!booking) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, width: 'min(380px, calc(100vw - 48px))',
      background: 'var(--bg-1, #131316)', border: '1px solid var(--gold, #D4A574)',
      borderRadius: 8, padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      zIndex: 5000,
    }}>
      <button onClick={dismiss} aria-label="Dismiss" style={{
        position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none',
        color: 'var(--text-3)', cursor: 'pointer', fontSize: 18,
      }}>×</button>

      <div style={{ fontSize: 11, letterSpacing: '0.15em', color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 8 }}>HOW WAS IT?</div>
      <div style={{ fontFamily: 'Fraunces, serif', fontSize: 18, marginBottom: 14, color: 'var(--text)' }}>
        Rate {booking.event_title}
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
        {[1, 2, 3, 4, 5].map(s => (
          <button key={s} onClick={() => setRating(s)} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: s <= rating ? 'var(--gold)' : 'var(--text-3)',
            fontSize: 28, padding: 0,
          }}>★</button>
        ))}
      </div>

      {rating > 0 && (
        <>
          <textarea
            value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder="Anything you'd like to share? (optional)"
            rows={3}
            style={{
              width: '100%', padding: 10, background: 'var(--bg-2)',
              border: '1px solid var(--line)', borderRadius: 4,
              color: 'var(--text)', fontFamily: 'Inter, sans-serif', fontSize: 13,
              resize: 'vertical', boxSizing: 'border-box',
            }}
          />
          <button onClick={submit} disabled={submitting} style={{
            width: '100%', marginTop: 10, padding: '10px 20px',
            background: 'var(--gold)', color: '#0A0A0B', border: 'none', borderRadius: 4,
            fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
            fontSize: 12, cursor: 'pointer',
          }}>{submitting ? 'Submitting…' : 'Submit review'}</button>
        </>
      )}
    </div>
  );
}
